/**
 * The wire contract for `POST /chat/campaigns/{campaign_id}`'s SSE stream
 * (`api` `fs-assistant-as-2`, `routers/chat.py`): eight event kinds, one JSON
 * object per `data:` line, each validated against its own schema so a field
 * the server renames surfaces as a parse failure here rather than a blank
 * card. `result`'s payload is validated against the tool's own response
 * schema from `../api/schemas` — the boundary the rest of the data plane
 * already reads through, never redefined.
 */

import { z } from 'zod';

import {
  amplificationSchema,
  commentsSchema,
  commentTermsSchema,
  compositionSchema,
  postMetricsSchema,
  postsSchema,
  sentimentSchema,
  territorySchema,
} from '../api/schemas';

export const TOOL_NAMES = [
  'get_post_metrics',
  'get_amplification',
  'get_posts',
  'get_composition',
  'get_campaign_composition',
  'get_comment_terms',
  'get_comments',
  'get_sentiment_series',
  'get_territory',
  'run_query',
  'get_counts',
  'render_chart',
] as const;
export type ToolName = (typeof TOOL_NAMES)[number];

/** `run_query`'s rows, and `get_counts`' with the days it counted; the database withheld
 *  every group below the anonymity threshold before either reached here. */
export const queryResultSchema = z.object({
  query_id: z.string(),
  rows: z.array(z.record(z.string(), z.unknown())),
  truncated: z.boolean(),
  window: z.object({ from: z.string().nullable(), to: z.string().nullable() }).optional(),
  sql: z.string().optional(),
});
export type QueryResult = z.infer<typeof queryResultSchema>;

export const CHART_KINDS = ['line', 'bar', 'stacked_bar', 'area', 'pie', 'table'] as const;

/** `render_chart`'s spec and the rows it plots. Checked again here, not trusted: a spec naming
 *  a column its rows lack fails the parse and drops only this card. */
export const chartResultSchema = z
  .object({
    chart: z.object({
      kind: z.enum(CHART_KINDS),
      title: z.string().nullable(),
      query_id: z.string().nullable(),
      x: z.string().min(1),
      series: z.string().nullable(),
      y: z.array(z.string().min(1)).min(1).max(6),
      y2: z.array(z.string().min(1)).max(2),
      units: z.record(z.string(), z.string()),
    }),
    rows: z.array(z.record(z.string(), z.unknown())).max(500),
    /** Which tool read the rows, and the days it read; the model never supplies rows. */
    provenance: z.object({
      tool: z.enum(['run_query', 'get_counts']),
      window: z.object({ from: z.string().nullable(), to: z.string().nullable() }).nullable(),
      /** The statement a `run_query` chart plotted, shown to the reader on request. */
      sql: z.string().nullable().optional(),
    }),
  })
  .refine(({ chart, rows }) =>
    [chart.x, ...chart.y, ...chart.y2, ...(chart.series === null ? [] : [chart.series])].every(
      (field) => rows.every((row) => field in row),
    ),
  )
  .refine(({ chart }) => chart.kind !== 'pie' || (chart.y.length === 1 && chart.y2.length === 0));
export type ChartResult = z.infer<typeof chartResultSchema>;

export function isToolName(value: string): value is ToolName {
  return (TOOL_NAMES as readonly string[]).includes(value);
}

/** One schema per tool's `result` payload, keyed by the name AS-1's loop
 *  sends — the same eight schemas the dashboard already validates against. */
export const RESULT_PAYLOAD_SCHEMAS: Record<ToolName, z.ZodType> = {
  get_post_metrics: postMetricsSchema,
  get_amplification: amplificationSchema,
  get_posts: postsSchema,
  get_composition: compositionSchema,
  get_campaign_composition: compositionSchema,
  get_comment_terms: commentTermsSchema,
  get_comments: commentsSchema,
  get_sentiment_series: sentimentSchema,
  get_territory: territorySchema,
  run_query: queryResultSchema,
  get_counts: queryResultSchema,
  render_chart: chartResultSchema,
};

export const deltaDataSchema = z.object({ text: z.string() });
export type DeltaData = z.infer<typeof deltaDataSchema>;

export const toolDataSchema = z.object({ name: z.string(), status: z.literal('called') });
export type ToolData = z.infer<typeof toolDataSchema>;

/** `payload` is validated against the named tool's own schema when the name
 *  is one of the eight declared tools; an undeclared name (a future tool
 *  ahead of this client) still parses, `payload` staying `unknown`.
 *
 *  A payload that fails its schema loses its card, never the turn: the tool
 *  hands the loop its own raw row (`get_posts` carries storage keys where the
 *  dashboard route carries signed URLs), and the model already read it, so
 *  the answer text is still whole. */
export const resultDataSchema = z
  .object({
    name: z.string(),
    payload: z.unknown().nullable(),
    error: z.string().nullable(),
    /** The window the tool was called with; absent on a turn stored before `api` sent it. */
    window: z.object({ from: z.string(), to: z.string() }).nullable().optional(),
  })
  .transform((result) => {
    if (result.payload === null || !isToolName(result.name)) return result;
    const parsed = RESULT_PAYLOAD_SCHEMAS[result.name].safeParse(result.payload);
    return parsed.success
      ? { ...result, payload: parsed.data }
      : { ...result, payload: null, error: 'payload outside the card contract' };
  });
export type ResultData = z.infer<typeof resultDataSchema>;

export const refusalReasonSchema = z.enum(['no_reading', 'tool_limit', 'unknown_tool']);
export type RefusalReason = z.infer<typeof refusalReasonSchema>;

export const refusalDataSchema = z.object({
  reason: refusalReasonSchema,
  would_have_read: z.array(z.string()),
});
export type RefusalData = z.infer<typeof refusalDataSchema>;

/** The turn's own continuation chips -- quoting detail (a window, a named
 *  bairro) only the answer just given actually carries, never derived from
 *  the tool name client-side. */
export const followupsDataSchema = z.object({ chips: z.array(z.string()) });
export type FollowupsData = z.infer<typeof followupsDataSchema>;

export const doneDataSchema = z.object({
  prompt_version: z.string(),
  tool_calls: z.number().int(),
});
export type DoneData = z.infer<typeof doneDataSchema>;

export const pingDataSchema = z.object({});
export type PingData = z.infer<typeof pingDataSchema>;

/** The turn failed; `message` is `api`'s own sentence for the reader. */
export const errorDataSchema = z.object({ message: z.string() });
export type ErrorData = z.infer<typeof errorDataSchema>;

export const EVENT_DATA_SCHEMAS = {
  delta: deltaDataSchema,
  tool: toolDataSchema,
  result: resultDataSchema,
  refusal: refusalDataSchema,
  followups: followupsDataSchema,
  done: doneDataSchema,
  ping: pingDataSchema,
  error: errorDataSchema,
} as const;

export type TurnEventKind = keyof typeof EVENT_DATA_SCHEMAS;

export function isTurnEventKind(kind: string): kind is TurnEventKind {
  return kind in EVENT_DATA_SCHEMAS;
}

export type TurnEvent =
  | { kind: 'delta'; data: DeltaData }
  | { kind: 'tool'; data: ToolData }
  | { kind: 'result'; data: ResultData }
  | { kind: 'refusal'; data: RefusalData }
  | { kind: 'followups'; data: FollowupsData }
  | { kind: 'done'; data: DoneData }
  | { kind: 'ping'; data: PingData }
  | { kind: 'error'; data: ErrorData };

/** The same eight kinds, as one schema — used to validate a `TurnEvent[]`
 *  read back from `sessionStorage`, never by the live stream parser above,
 *  which already knows its kind from the SSE frame it just read. */
export const turnEventSchema = z.union([
  z.object({ kind: z.literal('delta'), data: deltaDataSchema }),
  z.object({ kind: z.literal('tool'), data: toolDataSchema }),
  z.object({ kind: z.literal('result'), data: resultDataSchema }),
  z.object({ kind: z.literal('refusal'), data: refusalDataSchema }),
  z.object({ kind: z.literal('followups'), data: followupsDataSchema }),
  z.object({ kind: z.literal('done'), data: doneDataSchema }),
  z.object({ kind: z.literal('ping'), data: pingDataSchema }),
  z.object({ kind: z.literal('error'), data: errorDataSchema }),
]);

/** `ChatTurnIn` mirrored from `api`'s `schemas/chat.py`: what a turn sends,
 *  built by the caller from accumulated history plus a new question. */
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface TurnWindow {
  from: string;
  to: string;
}

export interface TurnRequest {
  tenant_id: string;
  question: string;
  history: ChatMessage[];
  window: TurnWindow | null;
  /** The post named by the URL's `?post=`, when the assistant opened scoped
   *  to one -- absent, not `null`, for a caller that predates the scope. */
  post_id?: string | null;
}
